import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const getOrCreateVisitorId = (): string => {
  const key = "visitor_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
};

const sendHeartbeat = (visitorId: string) => {
  supabase
    .from("active_visitors")
    .upsert({ visitor_id: visitorId, last_seen: new Date().toISOString() }, { onConflict: "visitor_id" })
    .then(({ error }) => {
      if (error) console.error("Heartbeat error:", error);
    });
};

/** Returns true if the current user is a logged-in admin. */
const checkIsAdmin = async (): Promise<boolean> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return false;
    const { data } = await supabase.rpc("has_role", {
      _user_id: session.user.id,
      _role: "admin",
    });
    return data === true;
  } catch {
    return false;
  }
};

const isLovablePreview = (): boolean => {
  const host = window.location.hostname;
  return (
    host.includes("lovableproject.com") ||
    host.includes("lovable.dev") ||
    host.includes("lovable.app")
  );
};

/** Wykrywa boty po User-Agent (Lighthouse/PageSpeed, crawlers, headless, stary Chrome). */
const isBotUA = (ua: string): boolean => {
  if (!ua) return false;
  if (/lighthouse|pagespeed|google-inspectiontool/i.test(ua)) return true;
  if (/moto g power \(2022\)/i.test(ua)) return true;
  if (/bot|crawl|spider|slurp|googlebot|bingbot|yandex|baidu|duckduck|facebookexternalhit|twitterbot|linkedinbot|semrush|ahrefs|mj12bot|dotbot|petalbot|bytespider|gptbot|claudebot/i.test(ua)) return true;
  if (/headless|phantomjs|puppeteer|playwright/i.test(ua)) return true;
  const m = ua.match(/Chrome\/(\d+)/);
  if (m && parseInt(m[1], 10) < 120) return true;
  return false;
};

const ADMIN_DEVICE_KEY = "is_admin_device";

const markAdminDevice = () => {
  try { localStorage.setItem(ADMIN_DEVICE_KEY, "1"); } catch { /* ignore */ }
};

const isAdminDevice = (): boolean => {
  try { return localStorage.getItem(ADMIN_DEVICE_KEY) === "1"; } catch { return false; }
};

export const usePageTracking = () => {
  const location = useLocation();

  // Track page visit
  useEffect(() => {
    if (isLovablePreview()) return; // Never track from Lovable preview

    const ua = navigator.userAgent || "";
    const isBot = isBotUA(ua);

    // Mark this device as admin permanently when /admin is visited (boty tu nie wpadną w praktyce)
    if (location.pathname.startsWith("/admin")) {
      if (!isBot) markAdminDevice();
      return; // and don't track this visit
    }

    // Nie trackujemy odwiedzin panelu właściciela ani stron auth - zaśmiecałyby statystyki schronisk
    if (
      location.pathname.startsWith("/panel") ||
      location.pathname.startsWith("/logowanie") ||
      location.pathname.startsWith("/rejestracja") ||
      location.pathname.startsWith("/zapomnialem-hasla") ||
      location.pathname.startsWith("/reset-hasla") ||
      location.pathname.startsWith("/przejmij")
    ) {
      return;
    }

    if (!isBot && isAdminDevice()) return; // Don't track admin devices

    const visitorId = getOrCreateVisitorId();

    const insertVisit = (botFlag: boolean) => {
      supabase
        .from("page_visits")
        .insert({
          visitor_id: visitorId,
          path: location.pathname,
          referrer: document.referrer || null,
          user_agent: ua || null,
          is_bot: botFlag,
        })
        .then(({ error }) => {
          if (error) console.error("Page tracking error:", error);
        });
    };

    // Boty: zapisujemy z flagą i pomijamy sprawdzanie roli admina (oszczędza requesty).
    if (isBot) {
      insertVisit(true);
      return;
    }

    checkIsAdmin().then((isAdmin) => {
      if (isAdmin) {
        markAdminDevice();
        return;
      }
      insertVisit(false);
    });
  }, [location.pathname]);

  // Heartbeat for active visitors — skip for admins, bots, and Lovable preview
  useEffect(() => {
    if (isLovablePreview()) return;
    if (isBotUA(navigator.userAgent || "")) return;
    if (isAdminDevice()) return;

    const visitorId = getOrCreateVisitorId();
    let interval: ReturnType<typeof setInterval> | null = null;

    checkIsAdmin().then((isAdmin) => {
      if (isAdmin) {
        markAdminDevice();
        return;
      }
      sendHeartbeat(visitorId);
      interval = setInterval(() => sendHeartbeat(visitorId), 30_000);
    });

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);
};
